import { create } from "zustand";
import { AppRecordJK, AppRecordStatusJK, RequestApi, RouteApi, StatusMsg } from "utils";
import { AppLog } from "services";
import { Service } from "utils/services";
import { requestSubmitFollowingUser } from "api-function/requestFriendStatusApi";

export type Tag = "like" | "share" | "comment" | "view";

interface DialogPostReactionState {
  likes: any[];
  shares: any[];
  comments: any[];
  views: any[];
  likeAppRecord: AppRecordJK;
  shareAppRecord: AppRecordJK;
  commentAppRecord: AppRecordJK;
  viewAppRecord: AppRecordJK;
  initializePage: (postId: string, tag: Tag) => Promise<void>;
  fetchPostReactions: (postId: string, tag: Tag, isRefresh: boolean) => Promise<void>;
  onFollowingUser: (userId: string, tag: Tag) => Promise<void>;
  disposePage: () => Promise<void>;
}

export const useDialogPostReactionStore = create<DialogPostReactionState>((set, get) => ({
  likes: [],
  shares: [],
  comments: [],
  views: [],
  likeAppRecord: new AppRecordJK(),
  shareAppRecord: new AppRecordJK(),
  commentAppRecord: new AppRecordJK(),
  viewAppRecord: new AppRecordJK(),

  initializePage: async (postId: string, tag: Tag) => {
    try {
      set({
        likes: [],
        shares: [],
        comments: [],
        views: [],
        likeAppRecord: new AppRecordJK(),
        shareAppRecord: new AppRecordJK(),
        commentAppRecord: new AppRecordJK(),
        viewAppRecord: new AppRecordJK(),
      });

      let tags: Tag[] = ["like", "share", "comment", "view"];
      tags = Service.moveToFirstNewArray(tags, tag);

      for (const tag of tags) {
        useDialogPostReactionStore.getState().fetchPostReactions(postId, tag, true);
      }
    } catch (error) {
      AppLog.show({
        message: `[initializePage]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  fetchPostReactions: async (postId: string, tag: Tag, isRefresh: boolean) => {
    const { likes, shares, comments, views, likeAppRecord, shareAppRecord, commentAppRecord, viewAppRecord } = get();

    let tmpLikeAppRecord = new AppRecordJK({ ...likeAppRecord });
    let tmpShareAppRecord = new AppRecordJK({ ...shareAppRecord });
    let tmpCommentAppRecord = new AppRecordJK({ ...commentAppRecord });
    let tmpViewAppRecord = new AppRecordJK({ ...viewAppRecord });

    try {
      let url: string = "";
      if (isRefresh) {
        url = RouteApi.getPostReaction;
        url += `token=${Service.token}`;
        url += `&post_id=${postId}`;
        url += `&scroll_tag=${tag}`;
      } else {
        if (tag === "like") {
          if (likeAppRecord.nextPageUrl) {
            url = likeAppRecord.nextPageUrl;
            tmpLikeAppRecord.loadingStatus = AppRecordStatusJK.loadingMore;
            set({
              likeAppRecord: tmpLikeAppRecord,
            });
          } else {
            return;
          }
        } else if (tag === "share") {
          if (shareAppRecord.nextPageUrl) {
            url = shareAppRecord.nextPageUrl;
            tmpShareAppRecord.loadingStatus = AppRecordStatusJK.loadingMore;
            set({
              shareAppRecord: tmpShareAppRecord,
            });
          } else {
            return;
          }
        } else if (tag === "comment") {
          if (commentAppRecord.nextPageUrl) {
            url = commentAppRecord.nextPageUrl;
            tmpCommentAppRecord.loadingStatus = AppRecordStatusJK.loadingMore;
            set({
              commentAppRecord: tmpCommentAppRecord,
            });
          } else {
            return;
          }
        } else if (tag === "view") {
          console.log("loadMoreItems view");
          if (viewAppRecord.nextPageUrl) {
            url = viewAppRecord.nextPageUrl;
            tmpViewAppRecord.loadingStatus = AppRecordStatusJK.loadingMore;
            set({
              viewAppRecord: tmpViewAppRecord,
            });
          } else {
            return;
          }
        }
      }

      const responses = await RequestApi(url);
      if (responses.status === "success") {
        const records = responses.records;
        if (isRefresh) {
          tmpLikeAppRecord.totalRecord = Service.toInteger(responses.records.no_user_like_count);
          tmpShareAppRecord.totalRecord = Service.toInteger(responses.records.no_user_share_count);
          tmpCommentAppRecord.totalRecord = Service.toInteger(responses.records.no_user_comment_count);
          tmpViewAppRecord.totalRecord = Service.toInteger(responses.records.no_user_view_count);
        }

        if (tag === "like") {
          tmpLikeAppRecord.nextPageUrl = records.likes.next_page_url;
          if (records.likes.next_page_url) {
            tmpLikeAppRecord.loadingStatus = AppRecordStatusJK.loaded;
          } else {
            tmpLikeAppRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
          }

          set({
            likeAppRecord: tmpLikeAppRecord,
            likes: [...likes, ...records.likes.data],
          });
        } else if (tag === "share") {
          tmpShareAppRecord.nextPageUrl = records.shares.next_page_url;
          if (records.shares.next_page_url) {
            tmpShareAppRecord.loadingStatus = AppRecordStatusJK.loaded;
          } else {
            tmpShareAppRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
          }

          set({
            shareAppRecord: tmpShareAppRecord,
            shares: [...shares, ...records.shares.data],
          });
        } else if (tag === "comment") {
          tmpCommentAppRecord.nextPageUrl = records.comments.next_page_url;
          if (records.comments.next_page_url) {
            tmpCommentAppRecord.loadingStatus = AppRecordStatusJK.loaded;
          } else {
            tmpCommentAppRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
          }

          set({
            commentAppRecord: tmpCommentAppRecord,
            comments: [...comments, ...records.comments.data],
          });
        } else if (tag === "view") {
          tmpViewAppRecord.nextPageUrl = records.views.next_page_url;
          if (records.views.next_page_url) {
            tmpViewAppRecord.loadingStatus = AppRecordStatusJK.loaded;
          } else {
            tmpViewAppRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
          }

          set({
            viewAppRecord: tmpViewAppRecord,
            views: [...views, ...records.views.data],
          });
        }
      } else {
      }
    } catch (error) {
      if (tag === "like") {
        tmpLikeAppRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
        set({
          likeAppRecord: tmpLikeAppRecord,
        });
      } else if (tag === "share") {
        tmpShareAppRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
        set({
          shareAppRecord: tmpShareAppRecord,
        });
      } else if (tag === "comment") {
        tmpCommentAppRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
        set({
          commentAppRecord: tmpCommentAppRecord,
        });
      } else if (tag === "view") {
        tmpViewAppRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
        set({
          viewAppRecord: tmpViewAppRecord,
        });
      }

      AppLog.show({
        message: `[fetchPostReactions]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  onFollowingUser: async (userId: string, tag: Tag) => {
    try {
      const { likes, shares, comments, views } = get();
      const responses = await requestSubmitFollowingUser({ userId: userId });
      if (responses.status === "success") {
        if (tag === "like") {
          const editIndex = likes.findIndex((like) => like.id === responses?.user?.id);
          if (editIndex !== -1) {
            set((state) => {
              const updatedLikes = [...state.likes];
              updatedLikes[editIndex] = {
                ...updatedLikes[editIndex],
                friend_status: responses?.user?.friend_status,
              };

              return { likes: updatedLikes };
            });
          }
        } else if (tag === "share") {
          const editIndex = shares.findIndex((share) => share.id === responses?.user?.id);
          if (editIndex !== -1) {
            set((state) => ({
              shares: state.shares.map((share, index) =>
                index === editIndex
                  ? {
                      ...share,
                      friend_status: responses?.user?.friend_status,
                    }
                  : share
              ),
            }));
          }
        } else if (tag === "comment") {
          const editIndex = comments.findIndex((comment) => comment.id === responses?.user?.id);
          if (editIndex !== -1) {
            set((state) => ({
              comments: state.comments.map((comment, index) =>
                index === editIndex
                  ? {
                      ...comment,
                      friend_status: responses?.user?.friend_status,
                    }
                  : comment
              ),
            }));
          }
        } else if (tag === "view") {
          const editIndex = views.findIndex((view) => view.id === responses?.user?.id);
          if (editIndex !== -1) {
            set((state) => ({
              views: state.views.map((view, index) => (index === editIndex ? { ...view, friend_status: responses?.user?.friend_status } : view)),
            }));
          }
        }
      } else {
      }
    } catch (error) {
      AppLog.show({
        message: `[onFollowingUser]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  disposePage: async () => {
    try {
      set({
        likes: [],
        shares: [],
        comments: [],
        views: [],
        likeAppRecord: new AppRecordJK(),
        shareAppRecord: new AppRecordJK(),
        commentAppRecord: new AppRecordJK(),
        viewAppRecord: new AppRecordJK(),
      });
    } catch (error) {
      AppLog.show({
        message: `[disposePage]`,
        status: StatusMsg.error,
        error,
      });
    }
  },
}));

